import { format, parseISO } from 'date-fns';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Customized,
} from 'recharts';
import type { CapacityPeriod } from '../hooks/useCapacity';
import { getUtilizationStatus } from '../hooks/useCapacity';

export interface SkillChartData {
  week: string;
  demandMin: number;
  demandMid: number;
  demandMax: number;
  bandHeight: number;
  capacity: number;
  utilizationPercent: number;
}

/**
 * Transform period data into chart-ready data for a single skill.
 * Pure function — no side effects, fully testable.
 *
 * Produces confidence band data: demandMin (invisible base), bandHeight
 * (visible shaded area), demandMax (for Y-axis scaling).
 */
export function transformSkillData(
  periods: CapacityPeriod[],
  skillId: string
): SkillChartData[] {
  return periods.map((period) => {
    const skill = period.bySkill.find((s) => s.skillId === skillId);
    const minHours = (skill?.demandedMinutesMin ?? 0) / 60;
    const maxHours = (skill?.demandedMinutesMax ?? 0) / 60;
    const capacityHours = (skill?.availableMinutes ?? 0) / 60;
    const midHours = (skill?.demandedMinutes ?? 0) / 60;
    const utilization = capacityHours > 0
      ? Math.round((midHours / capacityHours) * 100)
      : 0;

    return {
      week: formatWeekLabel(period.start),
      demandMin: Math.round(minHours * 10) / 10,
      demandMid: Math.round(midHours * 10) / 10,
      demandMax: Math.round(maxHours * 10) / 10,
      bandHeight: Math.round((maxHours - minHours) * 10) / 10,
      capacity: Math.round(capacityHours * 10) / 10,
      utilizationPercent: utilization,
    };
  });
}

function formatWeekLabel(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM d');
  } catch {
    return dateString;
  }
}

/** Aggregate utilization across all periods for a skill */
export function getSkillAggregateUtilization(
  periods: CapacityPeriod[],
  skillId: string
): number {
  let totalDemand = 0;
  let totalCapacity = 0;
  for (const period of periods) {
    const skill = period.bySkill.find((s) => s.skillId === skillId);
    totalDemand += skill?.demandedMinutes ?? 0;
    totalCapacity += skill?.availableMinutes ?? 0;
  }
  return totalCapacity > 0 ? Math.round((totalDemand / totalCapacity) * 100) : 0;
}

/** Check if a skill has any demand or capacity across all periods */
export function skillHasActivity(
  periods: CapacityPeriod[],
  skillId: string
): boolean {
  return periods.some((period) => {
    const skill = period.bySkill.find((s) => s.skillId === skillId);
    return (skill?.demandedMinutes ?? 0) > 0 || (skill?.availableMinutes ?? 0) > 0;
  });
}

const CHART_COLORS = {
  band: 'hsl(221, 83%, 53%)',
  demandMidLine: 'hsl(221, 83%, 53%)',
  capacityLine: 'hsl(215, 16%, 65%)',
};

interface SkillCapacityChartProps {
  skillId: string;
  skillName: string;
  periods: CapacityPeriod[];
}

export function SkillCapacityChart({ skillId, skillName, periods }: SkillCapacityChartProps) {
  const data = transformSkillData(periods, skillId);
  const aggregateUtil = getSkillAggregateUtilization(periods, skillId);
  const status = getUtilizationStatus(aggregateUtil);

  const statusColors: Record<string, string> = {
    low: 'text-foreground-muted',
    optimal: 'text-success',
    high: 'text-warning',
    overloaded: 'text-danger',
  };

  const badgeColors: Record<string, string> = {
    low: 'bg-gray-100 text-gray-600',
    optimal: 'bg-success-subtle text-success-foreground',
    high: 'bg-warning-subtle text-warning-foreground',
    overloaded: 'bg-danger-subtle text-danger-foreground',
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-background">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-base font-medium">{skillName}</h3>
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badgeColors[status]}`}>
          {aggregateUtil}%
        </span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" vertical={false} />
          <XAxis
            dataKey="week"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: 'hsl(214, 32%, 91%)' }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `${v}h`}
            width={45}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0]?.payload as SkillChartData;
              return (
                <div className="bg-background border border-border rounded-md px-3 py-2 shadow-sm text-sm">
                  <div className="font-medium">Week of {label}</div>
                  <div className="text-foreground-muted mt-1">
                    Min: {item.demandMin}h / Max: {item.demandMax}h
                  </div>
                  <div className="text-foreground-muted">
                    Capacity: {item.capacity}h
                  </div>
                  <div className={statusColors[getUtilizationStatus(item.utilizationPercent)]}>
                    {item.utilizationPercent}% utilized
                  </div>
                </div>
              );
            }}
          />
          {/* Confidence band: invisible base + visible shaded area */}
          <Area
            dataKey="demandMin"
            stackId="band"
            fill="transparent"
            stroke="none"
            activeDot={false}
          />
          <Area
            dataKey="bandHeight"
            stackId="band"
            fill={CHART_COLORS.band}
            fillOpacity={0.15}
            stroke="none"
            activeDot={false}
          />
          {/* Midpoint demand line — visible even when band is zero-width */}
          <Line
            dataKey="demandMid"
            stroke={CHART_COLORS.demandMidLine}
            strokeWidth={1.5}
            strokeOpacity={0.6}
            dot={false}
            activeDot={false}
            legendType="none"
          />
          {/* Hidden lines so Recharts includes demandMax and capacity in Y-axis domain */}
          <Line
            dataKey="demandMax"
            stroke="transparent"
            dot={false}
            activeDot={false}
            legendType="none"
          />
          <Line
            dataKey="capacity"
            stroke="transparent"
            dot={false}
            activeDot={false}
            legendType="none"
          />
          {/* Custom dashed capacity line — works with both band and point scales */}
          <Customized
            component={(props: Record<string, unknown>) => {
              const xAxisMap = props.xAxisMap as Record<string, { scale: (v: string) => number; bandSize?: number }> | undefined;
              const yAxisMap = props.yAxisMap as Record<string, { scale: (v: number) => number }> | undefined;
              if (!xAxisMap || !yAxisMap) return null;
              const xAxis = Object.values(xAxisMap)[0];
              const yAxis = Object.values(yAxisMap)[0];
              if (!xAxis?.scale || !yAxis?.scale) return null;

              // For point scales (Area), compute spacing from data points.
              // For band scales (Bar), use bandSize directly.
              const band = xAxis.bandSize || 0;
              const halfSpan = band > 0
                ? band / 2
                : data.length > 1
                  ? Math.abs(xAxis.scale(data[1]!.week) - xAxis.scale(data[0]!.week)) / 2
                  : 30;

              return (
                <g>
                  {data.map((point, i) => {
                    const cx = band > 0
                      ? xAxis.scale(point.week) + band / 2
                      : xAxis.scale(point.week);
                    const y = yAxis.scale(point.capacity);
                    return (
                      <g key={`cap-${i}`}>
                        {/* Horizontal dashed line centered on data point */}
                        <line
                          x1={cx - halfSpan}
                          y1={y}
                          x2={cx + halfSpan}
                          y2={y}
                          stroke={CHART_COLORS.capacityLine}
                          strokeWidth={2}
                          strokeDasharray="6 3"
                        />
                        {/* Vertical connector to next point when capacity changes */}
                        {i < data.length - 1 && data[i + 1] && (
                          <line
                            x1={cx + halfSpan}
                            y1={y}
                            x2={cx + halfSpan}
                            y2={yAxis.scale(data[i + 1]!.capacity)}
                            stroke={CHART_COLORS.capacityLine}
                            strokeWidth={2}
                            strokeDasharray="6 3"
                          />
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
